"use client";

import { ChevronRight, ReceiptText } from "lucide-react";
import { Button } from "@/components/ui/button";
import { DetailInfoIcon } from "@/components/ui/detail-info-row";
import {
  Drawer,
  DrawerClose,
  DrawerContent,
  DrawerDescription,
  DrawerFooter,
  DrawerHeader,
  DrawerTitle,
  DrawerTrigger,
} from "@/components/ui/drawer";

type Policy = {
  name: string;
  tiers: Array<{ hoursBefore: number; refundPercent: number }>;
  graceHours: number;
  allowsMemberSelfService: boolean;
  notes: string | null;
};

function tierLabel(tier: Policy["tiers"][number]) {
  const lead =
    tier.hoursBefore === 0
      ? "until the booking starts"
      : tier.hoursBefore % 24 === 0
        ? `${tier.hoursBefore / 24} day${tier.hoursBefore === 24 ? "" : "s"} before`
        : `${tier.hoursBefore} hours before`;
  return `${tier.refundPercent}% ${lead}`;
}

export function RefundPolicyDisclosure({
  policy,
  hasCashPayment,
}: {
  policy: Policy;
  hasCashPayment: boolean;
}) {
  const tiers = [...policy.tiers].sort((a, b) => b.hoursBefore - a.hoursBefore);

  return (
    <Drawer>
      <DrawerTrigger asChild>
        <button
          type="button"
          // The same card and glass well as the detail pages' grouped rows
          // (`DetailInfoGroup` + `DetailInfoRow size="sm"`) — it sits in the
          // venue page's "More" stack right above them.
          className="flex w-full items-center gap-3 rounded-2xl border border-border bg-surface px-4 py-3 text-left shadow-sm transition-colors hover:bg-surface-alt focus-visible:ring-2 focus-visible:ring-brand/30 focus-visible:outline-none dark:border-white/[0.1] dark:bg-white/[0.02] dark:shadow-none dark:hover:bg-white/[0.04]"
        >
          <DetailInfoIcon icon={ReceiptText} size="sm" />
          <span className="min-w-0 flex-1">
            <span className="block truncate text-sm font-medium text-text-primary dark:text-white">
              Refund policy
            </span>
            <span className="mt-0.5 block truncate text-xs text-text-secondary dark:text-white/55">
              {policy.name}
            </span>
          </span>
          <ChevronRight
            className="size-5 shrink-0 text-text-tertiary dark:text-white/40"
            aria-hidden="true"
          />
        </button>
      </DrawerTrigger>

      <DrawerContent
        layer={1200}
        className="mx-auto max-h-[85dvh] w-full max-w-[520px] overflow-hidden rounded-t-[28px]"
      >
        <DrawerHeader className="border-b border-border px-5 pt-3 pb-4 text-left dark:border-white/[0.1]">
          <DrawerTitle className="text-lg text-text-primary dark:text-white">
            Refund policy
          </DrawerTitle>
          <DrawerDescription className="text-text-secondary dark:text-white/55">
            {policy.name}
          </DrawerDescription>
        </DrawerHeader>

        <div className="overflow-y-auto px-5 py-5">
          <div className="space-y-4 text-sm leading-relaxed text-text-secondary dark:text-white/58">
            <div>
              <p className="font-medium text-text-primary dark:text-white/88">
                What you can get back
              </p>
              <p className="mt-1">
                {tiers.length > 0
                  ? tiers.map(tierLabel).join(" · ")
                  : "This booking is non-refundable."}
                {policy.graceHours > 0
                  ? ` Full refund within ${policy.graceHours} hours of purchase.`
                  : ""}
              </p>
            </div>

            <div>
              <p className="font-medium text-text-primary dark:text-white/88">
                Payment-processing cost
              </p>
              <p className="mt-1">
                {hasCashPayment
                  ? "If you cancel voluntarily, the exact original payment-processing cost is retained from your cash refund. If the organiser or Hiive cancels, you receive the full policy amount and they bear that cost."
                  : "No payment-processing deduction applies because no cash payment is being made."}
              </p>
            </div>

            {!policy.allowsMemberSelfService ? (
              <div>
                <p className="font-medium text-text-primary dark:text-white/88">
                  Cancellation review
                </p>
                <p className="mt-1">
                  Member cancellations require organiser approval. Your booking
                  remains active while the organiser reviews the request.
                </p>
              </div>
            ) : null}

            {policy.notes ? (
              <div>
                <p className="font-medium text-text-primary dark:text-white/88">
                  Additional terms
                </p>
                <p className="mt-1 whitespace-pre-wrap">{policy.notes}</p>
              </div>
            ) : null}
          </div>
        </div>

        <DrawerFooter className="border-t border-border px-5 pt-3 pb-[calc(env(safe-area-inset-bottom)+16px)] dark:border-white/[0.1]">
          <DrawerClose asChild>
            <Button type="button" className="h-11 w-full rounded-xl">
              Done
            </Button>
          </DrawerClose>
        </DrawerFooter>
      </DrawerContent>
    </Drawer>
  );
}
